import { Typography, Stack, Box } from "@mui/material";
import { IEventInfo } from "./EventCalendar";
import useIsAuthenticated from "react-auth-kit/hooks/useIsAuthenticated";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import BusinessIcon from "@mui/icons-material/Business";

interface IProps {
  event: IEventInfo;
}

const EventInfo = ({ event }: IProps) => {
  const isAuthenticated = useIsAuthenticated();

  const formatTime = (date: Date | undefined): string => {
    if (!date) return "";
    return date.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDate = (date: Date | undefined): string => {
    if (!date) return "";
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  };

  const areSameDay = (date1?: Date, date2?: Date): boolean => {
    if (!date1 || !date2) return false;
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  const textStyle = {
    color: "common.white",
    textShadow: "1px 1px 2px rgba(0,0,0,0.5)",
  };

  const renderEventTime = () => {
    if (!event.start || !event.end) return null;

    if (event.allDay) {
      if (areSameDay(event.start, event.end) || !event.end) {
        return (
          <Typography
            variant='caption'
            sx={textStyle}
            noWrap
          >
            All Day
          </Typography>
        );
      } else {
        // Multi-day all-day event
        return (
          <Typography
            variant='caption'
            sx={textStyle}
            noWrap
          >
            {formatDate(event.start)} - {formatDate(event.end)} (All Day)
          </Typography>
        );
      }
    }

    if (!areSameDay(event.start, event.end)) {
      // Spans multiple days, not all-day
      return (
        <Typography
          variant='caption'
          sx={textStyle}
          noWrap
        >
          {formatDate(event.start)} {formatTime(event.start)} -{" "}
          {formatDate(event.end)} {formatTime(event.end)}
        </Typography>
      );
    }

    // Single day, specific times
    return (
      <Typography
        variant='caption'
        sx={textStyle}
        noWrap
      >
        {formatTime(event.start)} - {formatTime(event.end)}
      </Typography>
    );
  };

  return (
    <Stack
      spacing={0.2}
      sx={{
        p: 0.5,
        overflow: "hidden",
        height: "100%",
      }}
    >
      {isAuthenticated() ? (
        <>
          {event.description && (
            <Typography
              variant='caption'
              sx={{ ...textStyle, fontWeight: "bold" }}
              noWrap
            >
              {event.description}
            </Typography>
          )}
          {event.companyName && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <BusinessIcon sx={{ fontSize: 12, ...textStyle }} />
              <Typography
                variant='caption'
                sx={textStyle}
                noWrap
              >
                {event.companyName}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <AccessTimeIcon sx={{ fontSize: 12, ...textStyle }} />
            {renderEventTime()}
          </Box>
        </>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            height: "100%",
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: 14, ...textStyle }} />
          <Typography
            variant='caption'
            sx={{ ...textStyle, fontStyle: "italic" }}
          >
            Private
          </Typography>
        </Box>
      )}
    </Stack>
  );
};

export default EventInfo;
